/**
 * Interface to interact with the macOS dock.
 *
 * @public
 */
import { checkIfCompatibleWithBackend, } from "@todesktop/client-util";
import { invoke } from "../invoke.js";
/**
 * Bounce this app icon in the dock.
 *
 * @see https://www.electronjs.org/docs/api/dock#dockbouncetype-macos
 * @returns An identifier representing the bounce executed.
 * @public
 */
export async function bounce(kind) {
    return window["todesktop"].app.dock.bounce(kind);
}
/**
 * Cancel the bounce action with identifier `id`.
 *
 * @see https://www.electronjs.org/docs/api/dock#dockcancelbounceid-macos
 * @public
 */
export async function cancelBounce(id) {
    return window["todesktop"].app.dock.cancelBounce(id);
}
/**
 * Hides the dock icon.
 * @see https://www.electronjs.org/docs/api/dock#dockhide-macos
 * @public
 */
export async function hide() {
    return invoke({
        namespace: "dock",
        method: "hide",
        args: [],
    });
}
/**
 * Shows the dock icon.
 * @see https://www.electronjs.org/docs/api/dock#dockshow-macos
 * @public
 */
export async function show() {
    return invoke({
        namespace: "dock",
        method: "show",
        args: [],
    });
}
/**
 * Set this app's badge in the dock.
 *
 * @see https://www.electronjs.org/docs/api/dock#docksetbadgetext-macos
 * @public
 */
export async function setBadge(label) {
    return window["todesktop"].app.dock.setBadge(label);
}
/**
 * Get the currently set badge for this app in the dock.
 *
 * @see https://www.electronjs.org/docs/api/dock#dockgetbadge-macos
 * @public
 */
export async function getBadge() {
    return window["todesktop"].app.dock.getBadge();
}
/**
 * Sets the `image` associated with this dock icon.
 *
 * @see https://www.electronjs.org/docs/latest/api/dock#dockseticonimage-macos
 * @example
 * ```typescript
 * import {
 *   app,
 *   nativeWindow,
 *   nativeImage,
 *   platform,
 * } from '@todesktop/client-core';
 *
 * // You can convert canvas to a data URL (using `toDataURL`)
 * const dataURL = "data:image/png;base64,iVBORw0KGgo…";
 *
 * const imgRef = await nativeImage.createFromDataURL(dataURL);
 *
 * if (platform.os.getOSPlatform() === "darwin") {
 *   await app.dock.setIcon(imgRef);
 * } else {
 *   // Windows and Linux
 *   await nativeWindow.setIcon({}, imgRef);
 * }
 * ```
 *
 * @public
 */
export async function setIcon(...args) {
    // Introduced in 3.33.0. See: https://github.com/ToDesktop/desktop-app/pull/201
    if (checkIfCompatibleWithBackend("dock:setIcon", "3.33.0")) {
        return invoke({
            namespace: "dock",
            method: "setIcon",
            args,
        });
    }
}
/**
 * Gets the `menu` associated with this dock icon.
 * @see https://www.electronjs.org/docs/api/dock#docksetmenu-macos
 * @public
 */
export async function getMenu() {
    return invoke({
        namespace: "dock",
        method: "getMenu",
        args: [],
    });
}
/**
 * Sets the `menu` associated with this dock icon.
 * @see https://www.electronjs.org/docs/api/dock#dockgetmenu-macos
 * @public
 */
export async function setMenu(menu) {
    return invoke({
        namespace: "dock",
        method: "setMenu",
        args: [menu],
    });
}
/**
 * @param isVisible - Whether the dock icon should be visible when no windows are open. Defaults to false.
 */
export function setVisibleWhenNoWindowsOpen(isVisible) {
    if (checkIfCompatibleWithBackend("dock:setVisibleWhenNoWindowsOpen", "6.7.0")) {
        return window["todesktop"]["dock:setVisibleWhenNoWindowsOpen"](isVisible);
    }
}
